import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, PRIMARY_OUTLET, Router, UrlSerializer } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountInfo, EventType, InteractionStatus } from '@azure/msal-browser';
import { devTools } from '@ngneat/elf-devtools';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Observable, Subject, debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs';
import LogRocket from 'logrocket';

import { MessageService, ToastMessageOptions } from 'primeng/api';

import { AppInsightsLoggerService, FeatureFlagService, LogoutWarningService } from './core';
import { AuthService } from './services/auth.service';
import { Cmg1003LookupsService } from './cmg-1003/services/cmg-1003-lookups.service';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { HandleUnrecoverableStateService } from './services/handle-unrecoverable-state.service';
import { Models } from './models/global.models';
import { NotificationService } from './services/notification.service';
import { AppSettingsService, CurrentUserService, SpinnerService } from './shared';
import { UiStore } from './core/state/ui/ui.store';
import { IntegratedLoginContext, SessionQuery, SessionStore } from './core/state';
import { BrowserDetectorService } from './services/browser-detector.service';
import { RouteApiService } from './pipeline/stores/api';
import { SecurityProfileQuery } from './loan-state';

@Component({
  selector: 'cmg-clear2-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _environment = inject(APP_ENV);
  private readonly _appInsightsLoggerService = inject(AppInsightsLoggerService);
  private readonly _apiAppService = inject(AppSettingsService);
  private readonly _authService = inject(AuthService);
  private readonly _cmg1003LookupsService = inject(Cmg1003LookupsService);
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _handleUnrecoverableStateService = inject(HandleUnrecoverableStateService);
  private readonly _logoutWarningService = inject(LogoutWarningService);
  private readonly _messageService = inject(MessageService);
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _router = inject(Router);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _titleService = inject(Title);
  private readonly _uiStore = inject(UiStore);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _browserDetectorService = inject(BrowserDetectorService);
  private readonly _routeApi = inject(RouteApiService);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);

  showNavbar$: Observable<boolean>;

  isLoading$ = this._spinnerService.count$.pipe(
    debounceTime(1),
    map((t) => t > 0),
  );

  loadingMessage$ = this._spinnerService.message$;
  zIndexSpinner$ = this._spinnerService.zIndex$;

  loanBannerMessages = signal<ToastMessageOptions[]>([]);

  private _destroyed$ = new Subject();

  constructor() {
    const urlSerializer = inject(UrlSerializer);

    if (!this._environment.production) {
      // devTools() allows use to see the store in the Redux Extension
      devTools({ name: 'CmgStore' });
    }

    this._titleService.setTitle('Clear');

    this.showNavbar$ = this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        const tree = urlSerializer.parse(event.urlAfterRedirects);
        const group = tree.root.children[PRIMARY_OUTLET];

        // no segments at /, which is the LoginComponent, which gives an undefined group
        if (!group) {
          return false;
        }

        const segments = group.segments;
        const path = segments[0].path;
        const ignoredPaths = ['404', 'auth', 'integrated-login', 'integrated-login-callback', 'login-failed', 'security-profiles', 'file-viewer'];
        const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte && path !== 'pipeline';

        return !ignoredPaths.includes(path) && !isIntegratedLogin;
      }),
      takeUntilDestroyed(),
    );

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          const pipelineUiState = JSON.parse(localStorage.getItem(Models.LocalStorage.PipelineUi));

          // Update Integrated Context Based on Query Param integratedContext passed in URL
          const url = urlSerializer.parse(event?.urlAfterRedirects);
          const integrationContext = url?.queryParams?.integrationContext?.toLocaleLowerCase();
          this._sessionStore.update({ integratedLoginContext: integrationContext });

          // Reset Pipeline's UI state when "Type" is not "AllLoans".
          if (pipelineUiState && pipelineUiState['activePipelineType'] !== 'AllLoans') {
            localStorage.removeItem(Models.LocalStorage.PipelineUi);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this._msalService.instance.enableAccountStorageEvents();

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter((event) => event.eventType === EventType.ACCOUNT_ADDED || event.eventType === EventType.ACCOUNT_REMOVED),
        tap(() => {
          if (this._msalService.instance.getAllAccounts().length === 0) {
            if (!this._authService.isIntegratedUrl()) {
              window.location.pathname = '/';
            } else {
              this._authService.logout();
            }
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this._msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        tap(() => {
          this._checkAndSetActiveAccount();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this._notificationService.showToast$
      .pipe(
        tap((x) => {
          this._messageService.add(x);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this._showSpinnerForNavigationEvents();
    this._checkBrowser();
  }

  ngOnInit() {
    this._uiStore.preInitialize();
    this._initLogRocketIfEnabled();

    setTimeout(() => {
      this._handleUnrecoverableStateService.handleUnrecoverableErrors();
    }, this._environment.serviceWorkerRegistrationDelay + 10000);

    // Log out warning timer (see environment.logoutWarning for config)
    this._logoutWarningService.initLogoutWarningTimer(() => {
      this._authService.logout();
    });

    this._getPartnersAccessForActiveProfile();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  logout() {
    this._authService.logout();
  }

  private _checkAndSetActiveAccount() {
    let activeAccount = this._msalService.instance.getActiveAccount();
    const accounts = this._msalService.instance.getAllAccounts();

    if (!activeAccount && accounts.length > 0) {
      activeAccount = accounts[0];
    }

    if (activeAccount) {
      this._msalService.instance.setActiveAccount(activeAccount);
      this._identifyLogRocketIfEnabled(activeAccount);
      this._featureFlagService.setupPolling();
      this._cmg1003LookupsService.initialize();
      this._apiAppService.getApiAppSettings();
      // Detect if external login flow is being used. If yes, skip this; allow
      // the external login component to handle resolving the token and security profile
      const isExternalLogin = window.location.href.includes('/integrated-login');
      if (!isExternalLogin) {
        this._authService.resolveSecurityProfile(activeAccount);
      }
      this._currentUserService
        .getUserSettings()
        .pipe(take(1))
        .subscribe((settings) => {
          this._sessionQuery.initUserSettings(settings);
        });
    }
  }

  private _showSpinnerForNavigationEvents() {
    this._router.events
      .pipe(
        takeUntilDestroyed(),
        tap((event) => {
          if (event instanceof NavigationStart) {
            this._spinnerService.show();
            return;
          }

          if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
            setTimeout(() => {
              this._spinnerService.hide();
            }, 0);
          }
        }),
      )
      .subscribe();
  }

  /**
   * Detect certain browsers and show warnings as necessary
   * @private
   */
  private _checkBrowser() {
    const isSafari = this._browserDetectorService.isSafari();
    const isChrome32Bit = this._browserDetectorService.isChrome32Bit();
    if (isSafari) {
      this.loanBannerMessages.update((value) => [
        ...value,
        {
          severity: 'warn',
          summary: 'Safari Warning!',
          detail: `<strong> Safari Warning! </strong> We're sorry, but this website is not fully supported in Safari. For the best experience, please switch to a supported browser like Microsoft Edge or Google Chrome. <b>Thank you for your understanding!</b>`,
        },
      ]);
    }
    if (isChrome32Bit) {
      this.loanBannerMessages.update((value) => [
        ...value,
        {
          severity: 'warn',
          summary: 'Unsupported Browser Detected',
          detail: `<strong> Unsupported Browser Detected </strong> Your current browser version is outdated and no longer supported. Please switch to Microsoft Edge for the best experience.`,
        },
      ]);
    }
  }

  private _initLogRocketIfEnabled() {
    if (this._environment.logRocket?.enabled && this._environment.logRocket?.applicationId) {
      try {
        LogRocket.init(this._environment.logRocket.applicationId, {
          mergeIframes: true,
          childDomains: ['*'],
          network: {
            requestSanitizer: (request) => {
              if (request?.body?.includes && request.body.includes('ssn')) {
                delete request.body;
              }
              return request;
            },
            responseSanitizer: (response) => {
              if (response?.body?.includes && response.body.includes('ssn')) {
                delete response.body;
              }
              return response;
            },
          },
        });
      } catch {
        //
      }
    }
  }

  /** Identifies LogRocket for the specified user account. (Requires ACTIVE MSAL account.) */
  private _identifyLogRocketIfEnabled(account: AccountInfo) {
    // We should get here on every page load.
    if (this._environment.logRocket?.enabled && !!account) {
      // LogRocket.identify call placed in timeout here because without it, the sessions
      // seem to unreliably show the username and email when viewed in LogRocket
      setTimeout(() => {
        // LogRocket Docs: Call LogRocket.identify() on page loads to associate your own application-specific user ID with the active user.
        LogRocket.identify((account?.idTokenClaims as Models.Clear2IdTokenClaims)?.UserID, {
          // LogRocket Docs: If the name or email keys are given they will be displayed instead of the UID in the UI. They can also be used in the search box to quickly find user sessions
          name: account.name,
          email: account.username,

          // These are called 'User Traits' in LogRocket
          environment: account.environment,
          tenantId: account.tenantId,
          homeAccountId: account.homeAccountId,
          localAccountId: account.localAccountId,
          nativeAccountId: account.nativeAccountId,
        });
      }, 0);
    }
  }

  private _getPartnersAccessForActiveProfile() {
    this._securityProfileQuery
      .selectActive()
      .pipe(
        filter((profile) => !!profile),
        debounceTime(500),
        switchMap(() => this._routeApi.getPartnersPipelineAccess()),
        takeUntil(this._destroyed$),
      )
      .subscribe((partnersAccess) => {
        this._sessionStore.update({ partnersAccess });
      });
  }
}
